import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import CompressIcon from '@mui/icons-material/Compress';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MovieIcon from '@mui/icons-material/Movie';
import TranslateIcon from '@mui/icons-material/Translate';
import {
    Badge,
    Box,
    Chip,
    Collapse,
    Divider,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useState } from 'react';

import { useVideoProcessing } from '../../contexts/VideoProcessingContext';

const VideoProcessingPanel = () => {
  const [expanded, setExpanded] = useState(true);
  const { processingVideos, removeVideo, toggleExpanded, getCompletionPercentage } = useVideoProcessing();
  
  // If no videos are processing, don't render anything
  if (processingVideos.length === 0) return null;
  //console.log("VideoProcessingPanel rendering, videos:", processingVideos);

  // Why a dubbing failed, from the server; only failed audio has one
  const failReason = (video) =>
    video.type === 'audio' && video.dubbing === 'FAILED' ? video.workStopReason || video.stopReason || null : null;

  const getStatusIcon = (status) => {
    switch(status) {
      case 'COMPLETED':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'PENDING':
      case 'PROCESSING':
        return <HourglassEmptyIcon fontSize="small" color="primary" />;
      case 'FAILED':
        return <ErrorIcon fontSize="small" color="error" />;
      case 'SKIPPED':
        return <CheckCircleIcon fontSize="small" color="disabled" />;
      case 'NOT_STARTED':
        return <HourglassEmptyIcon fontSize="small" color="disabled" />;
      default:
        return <HourglassEmptyIcon fontSize="small" />;
    }
  };
  
  const formatTimeElapsed = (startDate) => {
    const start = new Date(startDate);
    const now = new Date();
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute';
    if (diffMins < 60) return `${diffMins} minutes`;
    
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours} hr${hours > 1 ? 's' : ''} ${mins} min${mins > 1 ? 's' : ''}`;
  };
  
  return (
    <Paper 
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: expanded ? 350 : 200,
        zIndex: 1000,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'width 0.3s ease'
      }}
    >
      {/* Header */}
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          backgroundColor: 'primary.main',
          color: 'white',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MovieIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle2">
            Processing Queue {processingVideos.length > 0 && 
              <Badge 
                badgeContent={processingVideos.length} 
                color="error"
                sx={{ ml: 1 }}
              />
            }
          </Typography>
        </Box>
        <IconButton size="small" color="inherit">
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
        </IconButton>
      </Box>
      
      {/* Content */}
      <Collapse in={expanded} timeout="auto">
        <List dense sx={{ p: 0 }}>
          {processingVideos.map((video) => (
            <React.Fragment key={video.id}>
              <ListItem
                sx={{
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <ListItemIcon>
                  {video.type === 'audio' ? <AudiotrackIcon color="primary" /> : <MovieIcon color="primary" />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'medium',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '180px'
                      }}
                    >
                      {video.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={getCompletionPercentage(video)} 
                        sx={{ mt: 0.5, mb: 0.5, height: 5, borderRadius: 5 }}
                      />
                      {failReason(video) ? (
                        <Tooltip title={failReason(video)}>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: '220px' }}
                          >
                            {failReason(video)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Started {formatTimeElapsed(video.addedAt)} ago
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Remove from list (processing will continue)">
                    <IconButton 
                      edge="end" 
                      size="small"
                      onClick={() => removeVideo(video.id)}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton 
                    edge="end" 
                    size="small"
                    onClick={() => toggleExpanded(video.id)}
                  >
                    {video.expanded ? 
                      <ExpandLessIcon fontSize="small" /> : 
                      <ExpandMoreIcon fontSize="small" />
                    }
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              
              <Collapse in={video.expanded} timeout="auto">
                <Box sx={{ p: 1, pl: 7, bgcolor: 'action.hover' }}>
                  {video.type !== 'audio' && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CompressIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="caption" sx={{ flex: 1 }}>
                          Video Compression
                        </Typography>
                        {getStatusIcon(video.compression)}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TranslateIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="caption" sx={{ flex: 1 }}>
                          Subtitle Generation
                        </Typography>
                        {getStatusIcon(video.translation)}
                      </Box>
                    </>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TranslateIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="caption" sx={{ flex: 1 }}>
                      {video.type === 'audio' ? 'Audio Dubbing' : 'Dubbing Xformation'}
                    </Typography>
                    {getStatusIcon(video.dubbing)}

                  </Box>
                  {failReason(video) && (
                    <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5, pl: 3.5 }}>
                      {failReason(video)}
                    </Typography>
                  )}
                  
                  {video.languages && video.languages.length > 0 && (
                    <Box sx={{ mt: 1, pl: 2 }}>
                      <Divider textAlign="left" sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Languages
                        </Typography>
                      </Divider>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {video.languages.map((lang, idx) => {
                          const tooltip = `${lang.name}: ${lang.status || 'N/A'}${lang.dubbingStatus ? ' • Dubbing Xformation: ' + lang.dubbingStatus : ''}`;
                          const isProcessing = (lang.dubbingStatus === 'PROCESSING' || lang.status === 'PROCESSING');
                          const color = lang.dubbingStatus
                            ? (lang.dubbingStatus === 'COMPLETED' ? 'success' : lang.dubbingStatus === 'PROCESSING' ? 'primary' : lang.dubbingStatus === 'FAILED' ? 'error' : lang.dubbingStatus === 'SKIPPED' ? 'disabled' : 'default')
                            : (lang.status === 'READY' ? 'success' : lang.status === 'FAILED' ? 'error' : 'default');
                          const chipVariant = isProcessing ? 'filled' : 'outlined';

                          return (
                            <Tooltip key={idx} title={tooltip}>
                              <Chip
                                label={lang.name}
                                size="small"
                                color={color}
                                variant={chipVariant}
                                sx={{
                                  height: 20,
                                  '& .MuiChip-label': {
                                    px: 1,
                                    fontSize: '0.65rem'
                                  }
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
};

export default VideoProcessingPanel;